import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  QueryStateBoundary,
} from "@/components/ui";
import { centsToUsd } from "@/lib/money";
import { useQuote, useSendQuote, useMarkQuoteViewed, useAcceptQuote, useDeclineQuote } from "@/api/hooks/useQuotes";
import { usePackages } from "@/api/hooks/usePackages";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { QUOTE_STATUS_BADGE_VARIANT, QUOTE_STATUS_LABELS } from "@/api/types/quote";
import type { Quote } from "@/api/types/quote";
import { DeclineQuoteDialog } from "./DeclineQuoteDialog";
import { QuoteBuilderDialog } from "./QuoteBuilderDialog";

function formatTimestamp(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: quote, isLoading, error, refetch } = useQuote(id);
  const { data: packages } = usePackages();

  const [declineOpen, setDeclineOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const sendQuote = useSendQuote();
  const markViewed = useMarkQuoteViewed();
  const acceptQuote = useAcceptQuote();
  const declineQuote = useDeclineQuote();

  const packageName = packages?.find((p) => p.id === quote?.package_id)?.name;

  async function handleSend() {
    if (!quote) return;
    try {
      await sendQuote.mutateAsync({ id: quote.id });
      toastSuccess("Quote sent");
    } catch (err) {
      toastError("Couldn't send quote", getErrorMessage(err));
    }
  }

  async function handleMarkViewed() {
    if (!quote) return;
    try {
      await markViewed.mutateAsync({ id: quote.id });
      toastSuccess("Marked as viewed");
    } catch (err) {
      toastError("Couldn't update quote", getErrorMessage(err));
    }
  }

  async function handleAccept() {
    if (!quote) return;
    try {
      await acceptQuote.mutateAsync({ id: quote.id });
      toastSuccess("Quote accepted");
    } catch (err) {
      toastError("Couldn't accept quote", getErrorMessage(err));
    }
  }

  async function handleDecline(reason: string) {
    if (!quote) return;
    try {
      await declineQuote.mutateAsync({ id: quote.id, reason });
      toastSuccess("Quote declined");
      setDeclineOpen(false);
    } catch (err) {
      toastError("Couldn't decline quote", getErrorMessage(err));
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6 sm:p-8">
      <Button variant="link" size="sm" onClick={() => navigate("/quotes")}>
        ← Back to quotes
      </Button>

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {quote && (
          <QuoteDetailCard
            quote={quote}
            packageName={packageName}
            onSend={handleSend}
            onMarkViewed={handleMarkViewed}
            onAccept={handleAccept}
            onDeclineClick={() => setDeclineOpen(true)}
            onEditClick={() => setEditOpen(true)}
            isSending={sendQuote.isPending}
            isMarkingViewed={markViewed.isPending}
            isAccepting={acceptQuote.isPending}
          />
        )}
      </QueryStateBoundary>

      <DeclineQuoteDialog
        open={declineOpen}
        onCancel={() => setDeclineOpen(false)}
        onConfirm={(reason) => void handleDecline(reason)}
        isSubmitting={declineQuote.isPending}
      />

      {quote && (
        <QuoteBuilderDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          existingQuote={quote}
          onSaved={() => setEditOpen(false)}
        />
      )}
    </div>
  );
}

interface QuoteDetailCardProps {
  quote: Quote;
  packageName?: string;
  onSend: () => void;
  onMarkViewed: () => void;
  onAccept: () => void;
  onDeclineClick: () => void;
  onEditClick: () => void;
  isSending: boolean;
  isMarkingViewed: boolean;
  isAccepting: boolean;
}

function QuoteDetailCard({
  quote,
  packageName,
  onSend,
  onMarkViewed,
  onAccept,
  onDeclineClick,
  onEditClick,
  isSending,
  isMarkingViewed,
  isAccepting,
}: QuoteDetailCardProps) {
  const canEdit = quote.status === "draft";
  const canSend = quote.status === "draft";
  const canMarkViewed = quote.status === "sent";
  const canAccept = quote.status === "sent" || quote.status === "viewed";
  const canDecline = quote.status === "sent" || quote.status === "viewed";
  const pdfHref = `/api/v2/quotes/${quote.id}/pdf`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{packageName ?? "Quote"}</CardTitle>
        <Badge variant={QUOTE_STATUS_BADGE_VARIANT[quote.status]} data-testid="quote-status-badge">
          {QUOTE_STATUS_LABELS[quote.status]}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-md border border-copper/30 bg-copper/5 px-3 py-2 text-sm text-text-secondary">
          Your rental includes the venue space and setup — Fox &amp; Owl handles the rest.
          A COI is available on request.
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-text-primary">Line items</h3>
          <div className="divide-y divide-border rounded-md border border-border">
            {quote.line_items.map((li, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                <span className="text-text-primary">
                  {li.label}
                  <span className="ml-2 text-text-muted">× {li.qty}</span>
                </span>
                <span className="text-text-secondary">{centsToUsd(li.amount_cents)}</span>
              </div>
            ))}
          </div>
        </div>

        <dl className="space-y-1 text-sm">
          <div className="flex justify-between">
            <dt className="text-text-secondary">Subtotal</dt>
            <dd className="font-medium text-text-primary">{centsToUsd(quote.subtotal_cents)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-text-secondary">Service charge ({quote.service_charge_pct}%)</dt>
            <dd className="font-medium text-text-primary">
              {centsToUsd(quote.service_charge_cents)}
            </dd>
          </div>
          <div className="flex justify-between border-t border-border pt-1">
            <dt className="font-semibold text-text-primary">Total</dt>
            <dd className="font-semibold text-text-primary" data-testid="quote-total">
              {centsToUsd(quote.total_cents)}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-text-secondary">Deposit due ({quote.deposit_pct}%)</dt>
            <dd className="font-medium text-text-primary" data-testid="quote-deposit">
              {centsToUsd(quote.deposit_cents)}
            </dd>
          </div>
        </dl>

        <div className="space-y-1 text-xs text-text-muted">
          {formatTimestamp(quote.sent_at) && <p>Sent {formatTimestamp(quote.sent_at)}</p>}
          {formatTimestamp(quote.viewed_at) && <p>Viewed {formatTimestamp(quote.viewed_at)}</p>}
          {formatTimestamp(quote.accepted_at) && <p>Accepted {formatTimestamp(quote.accepted_at)}</p>}
          {formatTimestamp(quote.expires_at) && <p>Expires {formatTimestamp(quote.expires_at)}</p>}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
          {quote.pdf_key ? (
            <a
              href={pdfHref}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-primary hover:underline"
            >
              Download PDF
            </a>
          ) : (
            <span className="text-sm text-text-muted">PDF not generated yet</span>
          )}

          <div className="ml-auto flex flex-wrap gap-2">
            {canEdit && (
              <Button variant="secondary" size="sm" onClick={onEditClick}>
                Edit
              </Button>
            )}
            {canSend && (
              <Button size="sm" onClick={onSend} disabled={isSending}>
                {isSending ? "Sending..." : "Send"}
              </Button>
            )}
            {canMarkViewed && (
              <Button variant="secondary" size="sm" onClick={onMarkViewed} disabled={isMarkingViewed}>
                {isMarkingViewed ? "Saving..." : "Mark viewed"}
              </Button>
            )}
            {canAccept && (
              <Button size="sm" onClick={onAccept} disabled={isAccepting}>
                {isAccepting ? "Saving..." : "Accept"}
              </Button>
            )}
            {canDecline && (
              <Button variant="danger" size="sm" onClick={onDeclineClick}>
                Decline
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
